if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.pendingMonogramSubmit = false;
        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer') || document.querySelector('cart-items');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.giftCardCheckbox = document.querySelector('#pdp_giftCard_checkbox');
        this.giftCardSelectTrigger = document.querySelector('.custom-giftcard-select__trigger span');
        this.giftCardNote = document.querySelector('#pdp-giftCard-note');
        this.template = this.dataset.template;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (!this.pendingMonogramSubmit) {
          this.clearMonogramProperties();
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        if (!this.variantIdInput.value) {
          this.handleErrorMessage('Please select a variant');
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const selectedVariantId = formData.get('id');
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: selectedVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: selectedVariantId,
                cartData: response,
              });
            this.error = false;

            // Add gift packaging or card product if enabled
            let giftResponse = null;
            if (this.giftCardCheckbox?.checked && this.giftCardSelectTrigger) {
              if (this.giftCardNote?.value && this.giftCardNote?.value.length > 0) {
                await this.updateCartAttributes({ _giftPackageNote: this.giftCardNote?.value });
              }

              giftResponse = await this.addGiftProductIfEnabled(selectedVariantId);
            }

            // Add monogram fee if this is a monogram product
            let monogramFeeResponse = null;
            if (this.isMonogramProduct(formData)) {
              monogramFeeResponse = await this.addMonogramFeeIfNeeded(giftResponse || response);
            }

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(async () => {
                    const shippingProtection = document.querySelector('cart-shipping-protection');
                    shippingProtection?.updateCartTotal(monogramFeeResponse ?? giftResponse ?? response);
                    const shippingProtectionData = await shippingProtection?.updateShippingProtection();

                    this.cart.renderContents(shippingProtectionData ?? monogramFeeResponse ?? giftResponse ?? response);
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              const shippingProtection = document.querySelector('cart-shipping-protection');
              shippingProtection?.updateCartTotal(monogramFeeResponse ?? giftResponse ?? response);
              const shippingProtectionData = await shippingProtection?.updateShippingProtection();

              this.cart.renderContents(shippingProtectionData ?? monogramFeeResponse ?? giftResponse ?? response);
            }

            // update social proof count
            await window.updateSocialCount(response.product_id);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.pendingMonogramSubmit = false;
            if (!this.error) {
              this.clearMonogramProperties();
            }
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      clearMonogramProperties() {
        this.form?.querySelectorAll('[data-monogram-property]').forEach((input) => input.remove());
      }

      async addGiftProductIfEnabled(parentVariantId) {
        try {
          const giftCardProductId = this.giftCardSelectTrigger.getAttribute('product-id');
          if (!giftCardProductId) return;

          const normalizedParentVariantId = parentVariantId ? String(parentVariantId) : '';

          const properties = {
            'Gift Note': this.giftCardNote?.value || '',
          };

          if (normalizedParentVariantId) {
            properties.__gift_parent_variant_id = normalizedParentVariantId;
          }

          const body = JSON.stringify({
            id: giftCardProductId,
            quantity: 1,
            properties,
            sections: this.cart.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          });

          const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Error adding gift card product:', errorText);
            return;
          }

          const responseJson = await response.json();

          const giftPacking = document.querySelector('gift-package');
          giftPacking?.hide();

          this.giftCardCheckbox.checked = false;

          return responseJson;
        } catch (error) {
          console.error('Error adding gift card product:', error);
        }
      }

      async updateNote(note) {
        const body = JSON.stringify({ note: note });
        await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }

      async updateCartAttributes(attributes) {
        const body = JSON.stringify({ attributes: attributes });
        await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }

      isMonogramProduct(formData) {
        // Check if the form data contains a "Preview" property
        for (let [key, value] of formData.entries()) {
          if (key === 'properties[Preview]' && value) {
            return true;
          }
        }
        return false;
      }

      async addMonogramFeeIfNeeded(cartResponse) {
        const MONOGRAM_FEE_PRODUCT_ID = parseInt(window.MONOGRAM_FEE_PRODUCT_ID);
        
        if (!MONOGRAM_FEE_PRODUCT_ID || isNaN(MONOGRAM_FEE_PRODUCT_ID)) {
          console.error('Monogram fee product ID not configured or invalid');
          return cartResponse;
        }
        
        try {
          // Get current cart state to count monogram products
          const currentCartResponse = await fetch(`${routes.cart_url}.js`);
          if (!currentCartResponse.ok) {
            console.error('Could not fetch current cart state');
            return cartResponse;
          }
          
          const currentCart = await currentCartResponse.json();
          
          // Count monogram products (items with "Preview" property)
          let monogramProductCount = 0;
          currentCart.items.forEach((item) => {
            if (item.properties && item.properties.Preview) {
              monogramProductCount += item.quantity;
            }
          });

          // Count existing monogram fee items
          let currentMonogramFeeQuantity = 0;
          let monogramFeeVariantId = null;
          currentCart.items.forEach((item) => {
            if (item.product_id === MONOGRAM_FEE_PRODUCT_ID) {
              currentMonogramFeeQuantity += item.quantity;
              monogramFeeVariantId = item.variant_id;
            }
          });

          // If monogram fee quantity matches monogram product count, no action needed
          if (monogramProductCount === currentMonogramFeeQuantity) {
            return cartResponse;
          }

          // Get monogram fee product data if we don't have the variant ID
          if (!monogramFeeVariantId) {
            const productResponse = await fetch(`/products/${window.MONOGRAM_FEE_PRODUCT_HANDLE}.js`);
            if (productResponse.ok) {
              const productData = await productResponse.json();
              monogramFeeVariantId = productData.variants[0].id;
            } else {
              console.error('Could not fetch monogram fee product data');
              return cartResponse;
            }
          }

          // Update monogram fee quantity to match monogram product count
          const body = JSON.stringify({
            updates: { [monogramFeeVariantId]: monogramProductCount },
            sections: this.cart.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          });

          const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });

          if (!response.ok) {
            console.error('Error updating monogram fee quantity');
            return cartResponse;
          }

          return await response.json();
        } catch (error) {
          console.error('Error adding monogram fee:', error);
          return cartResponse;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          //this.submitButton.classList.add('hidden');
          if (text) {
            this.submitButtonText.textContent = text;
            this.submitButton.classList.add('hidden');
          } 
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.classList.remove('hidden');
          this.submitButtonText.textContent = this.template == "giftcard" ? window.variantStrings.send_to_me :window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
